import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, OnInit, AfterViewInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { DwComponent, DwComponentType } from '@devwareapps/devware-cap';
import { environment } from '../../../../../environments/environment';

@DwComponent({
  key: 'document-pdf-viewer',
  name: 'Document PDF Viewer',
  componentType: DwComponentType.pageComponent,
  isGlobal: true,
  //parentItemName: [ AppMetaDataItemNames.ProcessorConfig ]
})
@Component({
  selector: 'app-document-pdf-viewer',
  template: `
    <iframe *ngIf="iframeSrc"
      #pdfIframe
      [src]="iframeSrc"
      class="document-pdf-iframe"
      allow="fullscreen"
      title="PDF Viewer"
      (load)="onIframeLoad()">
    </iframe>
  `,
  styles: [`
    :host {
      display: flex;
      flex: 1 1 auto;
      min-height: 0;
    }
    .document-pdf-iframe {
      flex: 1 1 auto;
      min-height: 0;
      width: 100%;
      border: 0;
      background: var(--surface-base);
      border-radius: var(--radius-lg);
      box-shadow:
        0 0 0 1px var(--border-subtle),
        var(--shadow-md);
    }
    /* Mobile: drop the iframe's card chrome — outer shell is full-bleed
       so the rounded contour and drop shadow only create awkward edges. */
    @media (max-width: 769px) {
      .document-pdf-iframe {
        border-radius: 0;
        box-shadow: none;
      }
    }
    /* Plain: no card chrome, for hosts that frame the viewer themselves (e.g. a preview modal). */
    :host(.document-pdf-viewer--plain) .document-pdf-iframe {
      border-radius: 0;
      box-shadow: none;
      background: #fff;
    }
  `]
})
export class DocumentPdfViewerComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Input() pdfUrl: string;
  @Input() initialPageNumber?: number;
  /** pdf.js zoom on open; 'page-fit' shows the whole page height. Default keeps existing viewers unchanged. */
  @Input() zoom: 'page-width' | 'page-fit' = 'page-width';
  /** White background with no grey surround or page shadow inside the viewer. */
  @Input() @HostBinding('class.document-pdf-viewer--plain') plain = false;
  /**
   * Only these page numbers are shown, and pdf.js fetches just the byte ranges it needs to draw them
   * instead of streaming the whole file. Null shows the whole document as before.
   */
  @Input() pages: number[] | null = null;

  @Output() pageChange = new EventEmitter<number>();
  @Output() pagesLoaded = new EventEmitter<number>();
  @Output() searchMatchCountChange = new EventEmitter<{ current: number; total: number }>();
  @Output() scaleChange = new EventEmitter<number>();

  @ViewChild('pdfIframe') pdfIframe?: ElementRef<HTMLIFrameElement>;

  iframeSrc: SafeUrl;
  baseUrl: string;
  mimeType: string;
  rawUrl: string;
  //isFullscreen = false;
  private mobileScrollLockClass = 'pdf-viewer-mobile-lock';
  private scrollLocked = false;
  private scrollLockTimeout: ReturnType<typeof setTimeout> | null = null;
  // pdfjs's PDFViewerApplication, captured once the iframe + viewer.html load.
  // Typed as `any` because the bundled viewer.js doesn't ship Angular-friendly types.
  private pdfApp: any = null;

  // Custom pinch-to-zoom interception. pdfjs5's TouchManager reports inverted
  // pinch distances on iOS Safari when running inside this iframe (spread =>
  // zoom-out instead of zoom-in). We intercept the gesture in the iframe's
  // contentDocument and drive `currentScale` ourselves. Same-origin only —
  // in prod the iframe loads cross-origin and this becomes a no-op.
  private pinchPointers = new Map<number, { x: number; y: number }>();
  private pinchAnchor: { distance: number; scale: number } | null = null;
  private removeLoadOptionsListener: (() => void) | null = null;
  private removePinchListeners: (() => void) | null = null;

  // pdfjs toolbar / sidebar are hidden via CSS in src/assets/scripts/pdfjs/web/viewer.css
  // (those Acrobat-plugin URL hash params don't apply to pdfjs's bundled viewer).
  // pagemode=none is a real pdfjs param — keeps the sidebar collapsed on load.
  constructor(private sanitizer: DomSanitizer, private httpClient: HttpClient, private location: Location) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.pdfUrl && !changes.pdfUrl.firstChange) {
      this.setupIframeSrc();
    }

    if (changes.pages && !changes.pages.firstChange && this.pdfApp) {
      this.applyPageFilter(this.pdfIframe?.nativeElement?.contentDocument);
    }

    if (changes.initialPageNumber && !changes.initialPageNumber.firstChange && this.pdfApp) {
      this.goToPage(this.getInitialPageNumber());
    }
  }

  ngOnInit(): void {
    this.setupIframeSrc();
  }

  ngAfterViewInit(): void {
    // Scroll to top after view is rendered so breadcrumb is visible
    if (this.isMobileViewport()) {
      this.scrollToTop();
    }
    // Delay scroll lock to allow page to fully stabilize
    this.scrollLockTimeout = setTimeout(() => {
      this.applyMobileScrollLock();
    }, 500);
  }

  ngOnDestroy(): void {
    if (this.scrollLockTimeout) {
      clearTimeout(this.scrollLockTimeout);
      this.scrollLockTimeout = null;
    }
    this.releaseMobileScrollLock();
    this.removePinchInterceptor();
    this.removeLoadOptionsListener?.();
  }

  private setupIframeSrc() {
    this.listenForViewerLoad();
    this.baseUrl = environment.pdfViewerScriptUrl;

    const rawUrl = `${this.baseUrl}/web/viewer.html?file=${this.pdfUrl}#${this.buildViewerOptions()}`;

    this.rawUrl = rawUrl;
    this.iframeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(rawUrl) as string;
  }

  /**
   * Called when the iframe finishes loading viewer.html. The pdfjs runtime
   * (PDFViewerApplication) becomes available on the iframe's contentWindow.
   * We wait for its initializedPromise before subscribing to its eventBus.
   */
  onIframeLoad(): void {
    const win = this.pdfIframe?.nativeElement?.contentWindow as any;
    if (!win) return;

    const attach = () => {
      const app = win.PDFViewerApplication;
      if (!app || !app.initializedPromise) {
        // viewer.js may not have set the global yet; retry briefly.
        setTimeout(attach, 50);
        return;
      }
      app.initializedPromise.then(() => {
        this.pdfApp = app;
        this.installPinchInterceptor();
        if (this.plain) {
          this.applyPlainViewerStyle(win.document);
        }
        this.applyPageFilter(win.document);
        const eventBus = app.eventBus;
        if (!eventBus) return;
        eventBus.on('pagesinit', () => {
          this.pagesLoaded.emit(app.pagesCount);
          this.pageChange.emit(app.page);
          // Emit the initial scale once the viewer settles. pdfjs sometimes
          // resolves the literal scale (e.g. 1.27) only after first layout,
          // so this seed avoids a "100%" flash before the real number lands.
          const initial = app.pdfViewer?.currentScale;
          if (typeof initial === 'number') this.scaleChange.emit(initial);
        });
        eventBus.on('scalechanging', (e: any) => {
          if (typeof e?.scale === 'number') this.scaleChange.emit(e.scale);
        });
        eventBus.on('pagechanging', (e: any) => {
          this.pageChange.emit(e.pageNumber);
        });
        // Search match count updates. pdfjs fires both `updatefindmatchescount`
        // (raw count) and `updatefindcontrolstate` (state + count). Either is
        // fine — we just emit whatever the latest payload reports.
        const emitMatches = (e: any) => {
          const m = e?.matchesCount || { current: 0, total: 0 };
          this.searchMatchCountChange.emit({ current: m.current ?? 0, total: m.total ?? 0 });
        };
        eventBus.on('updatefindmatchescount', emitMatches);
        eventBus.on('updatefindcontrolstate', emitMatches);
      });
    };
    attach();
  }

  goToPage(n: number): void {
    if (!this.pdfApp) return;
    const total = this.pdfApp.pagesCount;
    if (!total) return;
    const clamped = Math.max(1, Math.min(total, n));
    this.pdfApp.page = clamped;
  }

  private buildViewerOptions(): string {
    return `pagemode=none&disableAutoFetch=true&disableStream=false&zoom=${this.zoom}&page=${this.getInitialPageNumber()}`;
  }

  /**
   * pdf.js only reads disableAutoFetch/disableStream from the URL hash in debug mode, so for page-limited views they are
   * set on its options object instead. The viewer fires 'webviewerloaded' on the parent document right before it opens
   * the file, which is the documented point to change options.
   */
  private listenForViewerLoad(): void {
    this.removeLoadOptionsListener?.();
    this.removeLoadOptionsListener = null;
    if (!this.pages || !this.pages.length) return;
    const onLoaded = (event: Event) => {
      const source = (event as CustomEvent).detail?.source;
      if (!source || source !== this.pdfIframe?.nativeElement?.contentWindow) return;
      source.PDFViewerApplicationOptions?.set('disableAutoFetch', true);
      source.PDFViewerApplicationOptions?.set('disableStream', true);
    };
    document.addEventListener('webviewerloaded', onLoaded);
    this.removeLoadOptionsListener = () => document.removeEventListener('webviewerloaded', onLoaded);
  }

  /**
   * Hides every page outside `pages`. Hidden pages keep their place in the flow with zero height (not display: none),
   * because pdf.js binary-searches page offsets to find what is on screen and needs them to stay in order.
   */
  private applyPageFilter(doc: Document | null | undefined): void {
    if (!doc) return;
    doc.getElementById('dw-page-filter')?.remove();
    if (!this.pages || !this.pages.length) return;
    const keep = this.pages.map(n => `:not([data-page-number="${Number(n)}"])`).join('');
    const style = doc.createElement('style');
    style.id = 'dw-page-filter';
    style.textContent = `.pdfViewer .page${keep} { height: 0 !important; min-height: 0 !important; margin: 0 !important;`
      + ' border: 0 !important; overflow: hidden !important; visibility: hidden !important; }';
    doc.head.appendChild(style);
  }

  /** The viewer is served from /assets (same origin), so its grey canvas and page shadow can be overridden in place. */
  private applyPlainViewerStyle(doc: Document): void {
    if (!doc || doc.getElementById('dw-plain-viewer')) return;
    const style = doc.createElement('style');
    style.id = 'dw-plain-viewer';
    // !important: these beat pdf.js's own rules inside the iframe only.
    style.textContent = 'body, #outerContainer, #mainContainer, #viewerContainer { background: #fff !important; }'
      + ' .pdfViewer .page { border: 0 !important; box-shadow: none !important; }';
    doc.head.appendChild(style);
  }

  private getInitialPageNumber(): number {
    return this.initialPageNumber && this.initialPageNumber > 0 ? this.initialPageNumber : 1;
  }

  nextPage(): void {
    if (!this.pdfApp) return;
    this.goToPage(this.pdfApp.page + 1);
  }

  previousPage(): void {
    if (!this.pdfApp) return;
    this.goToPage(this.pdfApp.page - 1);
  }

  /**
   * Toggle pdfjs's annotation editor in HIGHLIGHT mode (mode value 9 in
   * pdfjs's AnnotationEditorType enum). When on, the user can drag across
   * text in the PDF to create yellow highlight overlays. Highlights are
   * stored in pdfjs's annotationStorage and persist for the session only —
   * they're lost on page reload unless we add persistence later.
   */
  setHighlightMode(on: boolean): void {
    if (!this.pdfApp || !this.pdfApp.eventBus) return;
    this.pdfApp.eventBus.dispatch('switchannotationeditormode', {
      source: this.pdfApp,
      mode: on ? 9 : 0,
    });
  }

  /**
   * Step the zoom level via pdfjs's own zoom handlers — same code path the
   * (hidden) toolbar buttons would trigger, so step size and MIN/MAX bounds
   * stay consistent with pdfjs defaults.
   */
  zoomIn(): void {
    if (!this.pdfApp?.eventBus) return;
    this.pdfApp.eventBus.dispatch('zoomin', { source: this.pdfApp });
  }

  zoomOut(): void {
    if (!this.pdfApp?.eventBus) return;
    this.pdfApp.eventBus.dispatch('zoomout', { source: this.pdfApp });
  }

  fitWidth(): void {
    if (!this.pdfApp?.pdfViewer) return;
    this.pdfApp.pdfViewer.currentScaleValue = 'page-width';
  }

  /**
   * Install our own two-finger pinch handler inside the iframe's contentDocument,
   * intercepting pointer/touch events before pdfjs's TouchManager sees them.
   * Required because that built-in handler reports inverted pinch deltas inside
   * this iframe on iOS Safari, so spreading fingers zooms the PDF out instead
   * of in. With our handler driving `currentScale` directly, the gesture
   * behaves the way iOS users expect.
   *
   * Cross-origin (production CDN) iframes block contentDocument access, in
   * which case this silently no-ops and pdfjs's (broken) handler stays in
   * effect — same behavior as before this change.
   */
  private installPinchInterceptor(): void {
    this.removePinchInterceptor();

    const iframe = this.pdfIframe?.nativeElement;
    const win = iframe?.contentWindow;
    let doc: Document | null = null;
    try {
      doc = iframe?.contentDocument || null;
    } catch {
      return;
    }
    if (!win || !doc) return;

    const opts: AddEventListenerOptions = { capture: true, passive: false };

    const distance = (): number => {
      const pts = Array.from(this.pinchPointers.values());
      if (pts.length < 2) return 0;
      return Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
    };

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      this.pinchPointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this.pinchPointers.size === 2 && this.pdfApp?.pdfViewer) {
        this.pinchAnchor = {
          distance: distance(),
          scale: this.pdfApp.pdfViewer.currentScale,
        };
        e.stopImmediatePropagation();
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      if (e.pointerType !== 'touch' || !this.pinchPointers.has(e.pointerId)) return;
      this.pinchPointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this.pinchPointers.size >= 2 && this.pinchAnchor && this.pdfApp?.pdfViewer) {
        e.stopImmediatePropagation();
        e.preventDefault();
        const ratio = distance() / this.pinchAnchor.distance;
        if (!isFinite(ratio) || ratio <= 0) return;
        const next = Math.max(0.25, Math.min(10, this.pinchAnchor.scale * ratio));
        this.pdfApp.pdfViewer.currentScale = next;
      }
    };

    const onPointerEnd = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      this.pinchPointers.delete(e.pointerId);
      if (this.pinchPointers.size < 2) this.pinchAnchor = null;
    };

    // Block pdfjs's TouchManager from also reacting. iOS fires both PointerEvents
    // and TouchEvents — pdfjs may listen for either, so neutralize multi-touch on
    // both channels.
    const onTouch = (e: TouchEvent) => {
      if (e.touches.length >= 2) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    };

    win.addEventListener('pointerdown', onPointerDown as EventListener, opts);
    win.addEventListener('pointermove', onPointerMove as EventListener, opts);
    win.addEventListener('pointerup', onPointerEnd as EventListener, opts);
    win.addEventListener('pointercancel', onPointerEnd as EventListener, opts);
    win.addEventListener('touchstart', onTouch as EventListener, opts);
    win.addEventListener('touchmove', onTouch as EventListener, opts);

    this.removePinchListeners = () => {
      win.removeEventListener('pointerdown', onPointerDown as EventListener, opts);
      win.removeEventListener('pointermove', onPointerMove as EventListener, opts);
      win.removeEventListener('pointerup', onPointerEnd as EventListener, opts);
      win.removeEventListener('pointercancel', onPointerEnd as EventListener, opts);
      win.removeEventListener('touchstart', onTouch as EventListener, opts);
      win.removeEventListener('touchmove', onTouch as EventListener, opts);
      this.pinchPointers.clear();
      this.pinchAnchor = null;
    };
  }

  private removePinchInterceptor(): void {
    if (this.removePinchListeners) {
      this.removePinchListeners();
      this.removePinchListeners = null;
    }
  }

  /**
   * Drive pdfjs's find logic directly via its eventBus, without surfacing
   * pdfjs's own findbar UI (we hide it via viewer.css). The chrome owns
   * the search input; this method is the bridge.
   */
  searchInDocument(query: string, opts: { findPrevious?: boolean; highlightAll?: boolean } = {}): void {
    if (!this.pdfApp || !this.pdfApp.eventBus) return;
    const isFollowUp = !!query && this.lastQuery === query;
    this.pdfApp.eventBus.dispatch('find', {
      source: this.pdfApp,
      type: isFollowUp ? 'again' : '',
      query,
      caseSensitive: false,
      entireWord: false,
      highlightAll: opts.highlightAll !== false,
      findPrevious: !!opts.findPrevious,
      matchDiacritics: false,
    });
    this.lastQuery = query;
  }

  private lastQuery: string = '';

  private isMobileViewport(): boolean {
    return typeof window !== 'undefined' && typeof document !== 'undefined' &&
      !!window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
  }

  private scrollToTop() : void {
     window.scrollTo({ top: 1, behavior: 'auto' });

      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }, 60);
  }

  private applyMobileScrollLock(): void {
    if (!this.isMobileViewport() || this.scrollLocked) {
      return;
    }

    // Keep breadcrumb/header accessible by snapping to top before locking scroll
     window.scrollTo({ top: 0, behavior: 'auto' });

    document.body.classList.add(this.mobileScrollLockClass);
    document.documentElement.classList.add(this.mobileScrollLockClass);
    this.scrollLocked = true;
  }

  private releaseMobileScrollLock(): void {
    if (!this.scrollLocked || typeof document === 'undefined') {
      return;
    }

    document.body.classList.remove(this.mobileScrollLockClass);
    document.documentElement.classList.remove(this.mobileScrollLockClass);
    this.scrollLocked = false;
  }

}
